import { MatchEvent } from './types';

export const isCanonicalOwnGoal = (event: MatchEvent): boolean =>
  event.type.primary === 'own_goal' && (event.type.secondary || []).includes('goal');

export const isKeyEvent = (event: MatchEvent): boolean => {
  const { type, shot, infraction } = event;
  const primary = type.primary?.toLowerCase();
  const secondary = (type.secondary || []).map((s) => s.toLowerCase());

  // Goals
  if (isCanonicalOwnGoal(event)) return true;
  if (shot?.isGoal) return true;

  // Shots on target
  if (primary === 'shot' && shot?.onTarget) return true;

  // Cards
  if (infraction?.yellowCard || infraction?.redCard) return true;
  if (primary === 'infraction' && (secondary.includes('yellow_card') || secondary.includes('red_card'))) return true;

  // Penalties
  if (secondary.includes('penalty')) return true;

  return false;
};

export const getEventBadge = (event: MatchEvent): { label: string; color: string } => {
  const { type, shot, infraction } = event;
  const primary = type.primary?.toLowerCase();
  const secondary = (type.secondary || []).map((s) => s.toLowerCase());

  if (isCanonicalOwnGoal(event)) {
    return { label: 'Own Goal', color: 'bg-green-600 text-white' };
  }
  if (shot?.isGoal) {
    return { label: 'GOAL', color: 'bg-green-600 text-white' };
  }
  if (primary === 'shot') {
    if (shot?.onTarget) return { label: 'Shot on Target', color: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' };
    return { label: 'Shot', color: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' };
  }
  if (infraction?.redCard || secondary.includes('red_card')) {
    return { label: 'Red Card', color: 'bg-red-600 text-white' };
  }
  if (infraction?.yellowCard || secondary.includes('yellow_card')) {
    return { label: 'Yellow Card', color: 'bg-yellow-400 text-yellow-900' };
  }
  if (primary === 'infraction' || primary === 'foul') {
    return { label: 'Foul', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' };
  }
  if (primary === 'pass') {
    return { label: 'Pass', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' };
  }
  if (primary === 'duel' || primary === 'ground_duel' || primary === 'aerial_duel') {
    return { label: 'Duel', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' };
  }
  if (primary === 'free_kick') {
    return { label: 'Free Kick', color: 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300' };
  }
  if (primary === 'corner') {
    return { label: 'Corner', color: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400' };
  }
  if (primary === 'goal_kick') {
    return { label: 'Goal Kick', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' };
  }
  if (primary === 'throw_in') {
    return { label: 'Throw In', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' };
  }
  if (primary === 'carry') {
    return { label: 'Carry', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300' };
  }
  if (primary === 'interception') {
    return { label: 'Interception', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' };
  }
  if (primary === 'clearance') {
    return { label: 'Clearance', color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' };
  }
  return { label: primary || 'Event', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' };
};

export const getEventDetail = (event: MatchEvent): string => {
  const { type, shot, pass, infraction } = event;
  const secondary = (type.secondary || []).join(', ').replace(/_/g, ' ');
  const parts: string[] = [];

  if (isCanonicalOwnGoal(event)) {
    parts.push('Own Goal');
    if (shot?.bodyPart) parts.push(shot.bodyPart.replace(/_/g, ' '));
    if (shot?.xg) parts.push(`xG: ${shot.xg.toFixed(2)}`);
    if (shot?.goalZone) parts.push(shot.goalZone.replace(/_/g, ' '));
  } else if (shot?.isGoal) {
    if (shot.bodyPart) parts.push(shot.bodyPart.replace(/_/g, ' '));
    if (shot.xg) parts.push(`xG: ${shot.xg.toFixed(2)}`);
    if (shot.goalZone) parts.push(shot.goalZone.replace(/_/g, ' '));
  } else if (shot) {
    if (shot.bodyPart) parts.push(shot.bodyPart.replace(/_/g, ' '));
    if (shot.onTarget) parts.push('on target');
    if (shot.xg) parts.push(`xG: ${shot.xg.toFixed(2)}`);
  } else if (pass) {
    if (pass.recipient?.name) parts.push(`to ${pass.recipient.name}`);
    if (pass.length) parts.push(`${Math.round(pass.length)}m`);
    if (pass.accurate === false) parts.push('inaccurate');
  } else if (infraction) {
    if (infraction.type) parts.push(infraction.type.replace(/_/g, ' '));
  }

  if (parts.length === 0 && secondary) {
    return secondary;
  }

  return parts.join(' | ');
};

export const PERIOD_LABELS: Record<string, string> = {
  '1H': '1st Half',
  '2H': '2nd Half',
  'E1': 'Extra Time 1st',
  'E2': 'Extra Time 2nd',
  'P': 'Penalties',
};
